import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const DIFFERENTIATORS = [
  {
    title: 'We show the workflow, not just the tool list',
    body: 'Most AI directories answer what exists. Wowrolla is built to answer what should be used together, in what order, and what that stack is expected to change operationally.',
  },
  {
    title: 'Decision-first, not hype-first',
    body: 'We optimize for faster, clearer buying decisions. That means practical trade-offs, expected time to first result, and honest fit guidance when a tool is not right.',
  },
  {
    title: 'Transparency is part of the product',
    body: 'Affiliate relationships are disclosed. Methodology is public. We separate recommendation fit from monetization so users can understand why a tool appears where it does.',
  },
  {
    title: 'Built to convert research into implementation',
    body: 'The product does not stop at discovery. Finder flows, AI Stack Prescriptions, Readiness & ROI, and consulting are designed to move users from curiosity to an implementation-ready next step.',
  },
];

const AUDIENCES = [
  'SMB teams reducing tool sprawl, duplicate subscriptions, and manual workflow drag.',
  'Founders and operators who need a shortlist they can actually implement without a long evaluation cycle.',
  'Agencies standardizing delivery stacks and selecting repeatable tools for client work.',
  'Enterprise or regulated teams that need more structure around readiness, rollout risk, and internal buy-in.',
];

const PRINCIPLES = [
  'Fit before fees: recommendation quality comes first.',
  'Workflow context beats feature bloat: a tool is only useful if it improves the operating system around it.',
  'Explainability matters: users should understand why a recommendation was made.',
  'Implementation reality counts: setup load, integrations, and maintenance are part of the decision.',
];

const AboutPage = () => {
  const methodologyFaq = [
    {
      q: 'How does Wowrolla rank AI tools?',
      a: 'Wowrolla evaluates tools for workflow fit, implementation complexity, price tier, transparency, and expected implementation confidence. Rankings are based on fit for the use case, not commission rate.',
    },
    {
      q: 'Does Wowrolla earn affiliate commissions?',
      a: 'Yes. We disclose affiliate relationships on tool pages and transparency sections. Commission does not override fit scoring.',
    },
    {
      q: 'How often are tool pages updated?',
      a: 'Core tool pages are reviewed regularly, with higher-priority tools refreshed more often when pricing, features, or positioning changes are detected.',
    },
    {
      q: 'Who is Wowrolla for?',
      a: 'Wowrolla is built for founders, operators, agencies, and enterprise teams that need to choose tools based on workflow outcomes, implementation reality, and budget constraints.',
    },
  ];

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-grid" data-testid="about-page">
      <SEOHead
        pageType="guide"
        title="About Wowrolla"
        description="How Wowrolla evaluates AI tools, discloses affiliate relationships, and helps teams move from tool research to workflow-ready implementation."
        slug="about"
        faqs={methodologyFaq}
        dateModified={new Date().toISOString()}
        author="Wowrolla Editorial"
        category="about"
      />

      <section className="container mx-auto px-6 pb-12 max-w-5xl">
        <h1 className="text-4xl font-black mb-4" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
          About Wowrolla
        </h1>
        <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
          Last verified: {new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })}
        </p>
        <p className="text-lg max-w-3xl leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          Wowrolla is a decision-first AI tool discovery and advisory platform. We help teams move from
          {' '}"there are too many tools" to "here is the workflow, the stack, the cost band, and the next step"
          {' '}without relying on generic hype or opaque rankings.
        </p>
      </section>

      <section className="container mx-auto px-6 pb-8 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {DIFFERENTIATORS.map((item) => (
            <div key={item.title} className="terminal-grid p-6">
              <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
                {item.title}
              </h2>
              <p className="leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {item.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="container mx-auto px-6 pb-8 max-w-5xl">
        <div className="terminal-grid p-6">
          <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
            How we evaluate recommendations
          </h2>
          <p className="leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            Wowrolla is designed around one practical question: what should this team do next? Our public product combines catalog data,
            workflow context, price-band awareness, implementation complexity, and recommendation reasoning. We do not treat tool selection as
            a list-ranking exercise detached from how teams actually operate.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)' }}>Core principles</p>
              <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
                {PRINCIPLES.map((principle) => (
                  <li key={principle}>- {principle}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)' }}>What users can expect</p>
              <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
                <li>- A finder flow that narrows options by workflow, team size, budget, and role.</li>
                <li>- AI Stack Prescriptions that show sequence, expected outcome, and implementation logic.</li>
                <li>- Consulting paths for teams that need higher-confidence stack design or rollout support.</li>
                <li>- Transparent disclosure standards documented on <Link to="/transparency" style={{ color: 'var(--accent-primary)' }}>Transparency</Link>.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-8 max-w-5xl">
        <div className="terminal-grid p-6">
          <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
            Why teams choose Wowrolla over generic directories
          </h2>
          <p className="leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            Generic comparison sites are useful for discovering products. They are less useful when the real question is implementation order,
            stack coordination, and operational payoff. Wowrolla is built to bridge that gap with workflow context, consulting paths, and
            sequential guidance rather than pure review-volume rankings.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[var(--bg-elevated)] border border-[var(--border-subtle)] p-4">
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Generic directories</p>
              <p style={{ color: 'var(--text-secondary)' }}>Tell you what tools exist and which ones have strong review volume.</p>
            </div>
            <div className="bg-[var(--bg-elevated)] border border-[var(--border-subtle)] p-4">
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Wowrolla</p>
              <p style={{ color: 'var(--text-secondary)' }}>Shows the workflow, the stack sequence, expected time to first result, and where consulting is actually warranted.</p>
            </div>
            <div className="bg-[var(--bg-elevated)] border border-[var(--border-subtle)] p-4">
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Outcome</p>
              <p style={{ color: 'var(--text-secondary)' }}>Fewer dead-end trials, faster shortlists, and more implementation-ready decisions.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-12 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="terminal-grid p-6">
            <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
              Who we help
            </h2>
            <ul className="space-y-3" style={{ color: 'var(--text-secondary)' }}>
              {AUDIENCES.map((audience) => (
                <li key={audience}>- {audience}</li>
              ))}
            </ul>
          </div>

          <div className="terminal-grid p-6">
            <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
              Trust and disclosure
            </h2>
            <p className="leading-relaxed mb-3" style={{ color: 'var(--text-secondary)' }}>
              Wowrolla participates in affiliate programs for some tools. That relationship is disclosed publicly and does not replace fit-based editorial judgment.
            </p>
            <p className="leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              If you want the full methodology, disclosure policy, and how we separate public guidance from internal business details, start with{' '}
              <Link to="/transparency" style={{ color: 'var(--accent-primary)' }}>Transparency</Link> or contact us directly via{' '}
              <Link to="/contact" style={{ color: 'var(--accent-primary)' }}>Contact</Link>.
            </p>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-12 max-w-5xl">
        <div className="terminal-grid p-6">
          <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
            What to do next
          </h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/find" className="btn-primary">Start with the Finder</Link>
            <Link to="/ai-stacks" className="px-4 py-3 border" style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' }}>Explore AI Stacks</Link>
            <Link to="/consulting#start" className="px-4 py-3 border" style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' }}>Book a discovery call</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;

